// src/belediye/components/notifications/NotificationToaster.js
//
// FAZ-33 — Sağ-üst toast katmanı. Panel layout'una BİR KEZ mount edilir.
// Redux belediyeNotifications.items'ı dinler; okunmamış + daha önce toast'lanmamış
// + toast açık + kaynağı muted olmayan adayları gösterir.
//
// Kurallar:
//  - BİR BİLDİRİM ÖMRÜNDE BİR KEZ toast'lanır (okunsun okunmasın). Toast'lanan
//    id'ler + su seviyesi localStorage'da (kullanıcı bazlı) → F5, yeni sekme,
//    yeni oturum TEKRAR patlatmaz (FAZ-83).
//  - 7 sn oto-kapanma; hover'da sayaç durur; aynı anda masaüstünde ≤4,
//    dar ekranda ≤2 görünür — fazlası kuyrukta bekler.
//  - Dismiss (oto/X) OKUNDU YAPMAZ. Tıklama → hedefe git + markRead([id]).
//  - level → renk/ikon. Ses: yeni-gelen + tercih açık + kaynak muted değil.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import {
  markRead,
  fetchNotifications,
  fetchPreferences,
  selectToastEnabled,
  selectSoundEnabled,
  selectMutedCategories,
} from "../../redux/slices/belediyeNotificationsSlice";
import {
  installSoundUnlock,
  playNotifySound,
} from "./notificationSound";
import {
  actorName,
  bildirimGovdesi,
  bildirimNotu,
  kayitEtiketi,
  sourceLabel,
} from "./notificationMeta";
// FAZ-69: toast satırı da sayfa/zil ile AYNI simge kuralını kullanır — kaynak
// ikonu (neyle ilgili), yalnız acil/uyarıda seviye ikonu (ne kadar acil).
import { sourceIcon, conversationIcon } from "../icons/appIcons";
import resolveNotificationTarget from "./resolveNotificationTarget";
import { openChat } from "../../services/mesajlasmaBridge";
import useModuleEnabled from "../../hooks/useModuleEnabled";
// FAZ-106 — bu CİHAZDA telefon bildirimi olarak gösterilmiş kimlikler.
import { defterKullanilabilir, gosterilenleriOku } from "../../services/pushDefteri";
import "./NotificationToaster.css";

// FAZ-83 — GÖRÜNÜR TAVAN ARTIK EKRANA GÖRE.
//
// Kullanıcı (2026-08-01): "mobilde daha nazik toastlar gelsin; çok kaba
// toastlar bunlar ve ekranı fazlasıyla kaplıyorlar."
//
// Dört kart masaüstünde sağ üstte küçük bir sütun, telefonda ekranın yarısı
// demekti (kart yüksekliği aynı, ekran üçte bir). Tavan tek sayı olamaz —
// ölçüye bağlanır. Aşan kısım DÜŞMEZ, kuyrukta bekler (aşağıdaki MAX_QUEUE).
const MAX_VISIBLE_WIDE = 4;
const MAX_VISIBLE_MOBILE = 2;
const MOBIL_SORGU = "(max-width: 560px)";
const AUTO_DISMISS_MS = 7000;

// FAZ-79 — KUYRUK.
//
// Kullanıcı (2026-07-30): "kişi giriş yaptığında orada dört tane toast görüyor,
// dörtten fazlasını göremiyor. Altı sekiz tane birikmiş varsa bunları peş peşe
// görsün — işte ilk dördü, diğeri gelsin falan; ya da bildirimlere tıklanması
// teşvik edilsin de hepsini görebilsin."
//
// Eski davranış: adayların TAMAMI "toast'landı" diye işaretlenip
// `.slice(0, MAX_VISIBLE)` ile ilk 4'ü gösteriliyordu — kalan 4 satır
// SESSİZCE DÜŞÜYORDU. Bir daha da hiç toast'lanmıyordu (işaret kalıcı).
//
// Yeni davranış: fazlası KUYRUKTA bekler; ekranda yer açıldıkça (oto-kapanma,
// X, tıklama) sırayla girer. Kuyrukta bekleyen varsa yığının altında
// "+N bildirim daha" çipi durur — tıklayınca bildirim merkezine götürür
// ("hepsini görebilsin" isteğinin karşılığı).
//
// Kuyruk tavanı: sonsuz birikme olmasın. 200 okunmamışla giriş yapan birine
// 200 toast göstermek işkence olurdu; tavanı aşan kısım zaten zilde duruyor ve
// çip "tümünü gör" diyor.
const MAX_QUEUE = 12;

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

const readUserIdSafe = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    const id = Number(u?.id);
    return Number.isFinite(id) && id > 0 ? id : 0;
  } catch {
    return 0;
  }
};

const toastedKey = () => `bildirim.toastedIds.v1:${readUserIdSafe() || "anon"}`;

const loadToasted = () => {
  try {
    const raw = localStorage.getItem(toastedKey());
    const arr = raw ? JSON.parse(raw) : [];
    return new Set((Array.isArray(arr) ? arr : []).map(Number).filter(Boolean));
  } catch {
    return new Set();
  }
};

const saveToasted = (set) => {
  try {
    // son 500 id yeterli (LS şişmesin)
    const arr = Array.from(set).slice(-500);
    localStorage.setItem(toastedKey(), JSON.stringify(arr));
  } catch {
    /* yut */
  }
};

// ── SU SEVİYESİ / high-water mark (FAZ-83) ─────────────────────────────────
//
// Kimlik listesi tek başına yeterli bir "bir kez göster" garantisi DEĞİLDİ:
//   • liste son 500 id ile budanıyor → 500 bildirim sonra eskiler geri döner;
//   • localStorage yazılamazsa (gizli sekme, kota) liste sessizce boş kalır.
// Her iki durumda da OKUNMAMIŞ eski kayıtlar yeniden toast'lanır — bildirilen
// kusurun ta kendisi.
//
// Su seviyesi = bugüne kadar toast'lanmış EN BÜYÜK id. Tek sayı, budanamaz.
// Kural: id ≤ seviye ise bir daha asla toast'lanmaz. Id'ler artan olduğu için
// bu "daha önce görüldü" ile eşdeğerdir; yeni kayıtlar her zaman üstündedir.
const hwmKey = () => `bildirim.toastHWM.v1:${readUserIdSafe() || "anon"}`;

const loadHwm = () => {
  try {
    const n = Number(localStorage.getItem(hwmKey()));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
};

const saveHwm = (n) => {
  try {
    localStorage.setItem(hwmKey(), String(n));
  } catch {
    /* yut */
  }
};

/** Ekran dar mı (canlı dinler — döndürme/pencere boyu değişimi anında yansır). */
function useMobil() {
  const olc = () => {
    try {
      return !!window.matchMedia?.(MOBIL_SORGU)?.matches;
    } catch {
      return false;
    }
  };
  const [mobil, setMobil] = useState(olc);
  useEffect(() => {
    let mq = null;
    try {
      mq = window.matchMedia?.(MOBIL_SORGU) || null;
    } catch {
      mq = null;
    }
    if (!mq) return undefined;
    const dinle = (e) => setMobil(!!e.matches);
    // Safari 13 ve altı yalnız addListener biliyor.
    if (mq.addEventListener) mq.addEventListener("change", dinle);
    else if (mq.addListener) mq.addListener(dinle);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", dinle);
      else if (mq.removeListener) mq.removeListener(dinle);
    };
  }, []);
  return mobil;
}

const LEVEL_ICON = {
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  critical: ExclamationCircleIcon,
  info: InformationCircleIcon,
};

/** Toast simgesi: acil/uyarıda seviye, diğerlerinde kaynak (mesajda sohbet türü). */
function toastIcon(t) {
  const lvl = safeStr(t?.level) || "info";
  if (lvl === "critical" || lvl === "warning") {
    return LEVEL_ICON[lvl] || InformationCircleIcon;
  }
  const src = safeStr(t?.source_module);
  if (src === "mesajlasma") return conversationIcon(safeStr(t?.payload?.kind));
  return sourceIcon(src);
}

/** Saat (SS:DD) — toast "ne zaman"ı da söylesin; eskiden hiç yazmıyordu. */
const saatTR = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

// FAZ-49: cancelled toast'ı da gösterilir (bkz. slice/Bell aynı karar).
const isCancelled = () => false;

// ── SESSİZ SAATLER (FAZ-79) ────────────────────────────────────────────────
//
// `NotificationPreference.quiet_hours_start/end` FAZ-31'den beri modelde ve
// uçta duruyordu ama HİÇBİR yüzey okumuyordu → ölü alan. Ayarlar penceresine
// anahtar eklendiği için burada da uygulanması gerekir; yoksa kullanıcı
// kapattığını sanıp toast almaya devam eder.
//
// Kural: aralık İÇİNDEYSE toast çizilmez ve ses çalmaz — bildirimin kendisi
// yine üretilir ve zilde birikir (bildirim kaybı YOK, yalnız kesinti yok).
// Gece yarısını aşan aralık (22:00–08:00) desteklenir: başlangıç > bitiş ise
// koşul "≥ başlangıç VEYA < bitiş" olur.
const _dakika = (hhmm) => {
  const m = String(hhmm || "").match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const s = Number(m[1]);
  const d = Number(m[2]);
  if (!Number.isFinite(s) || !Number.isFinite(d)) return null;
  return s * 60 + d;
};

export const sessizSaatteMi = (start, end, now = new Date()) => {
  const b = _dakika(start);
  const s = _dakika(end);
  // Tek uç doluysa kural yok — yarım tanımlı aralıkla tahmin yürütmeyiz.
  if (b === null || s === null) return false;
  if (b === s) return false; // sıfır uzunluk = kural yok
  const t = now.getHours() * 60 + now.getMinutes();
  return b < s ? t >= b && t < s : t >= b || t < s;
};

// ── Tek toast kartı (kendi 7sn sayacı; hover'da durur) ─────────────────────
function ToastCard({ toast, onClose, onClick }) {
  const timerRef = useRef(null);
  const [paused, setPaused] = useState(false);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (paused) {
      clear();
      return;
    }
    timerRef.current = setTimeout(() => onClose(toast.id), AUTO_DISMISS_MS);
    return clear;
    // paused değişince sayaç yeniden kurulur
  }, [paused, toast.id, onClose]);

  const Icon = toastIcon(toast);
  const saat = saatTR(toast.created_at);

  return (
    <div
      className={`bt-toast bt-level-${toast.level || "info"}`}
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button
        type="button"
        className="bt-toast-body"
        onClick={() => onClick(toast)}
        title="Aç"
      >
        <span className="bt-toast-ic">
          <Icon />
        </span>
        <span className="bt-toast-texts">
          {/* Üst satır: NEREDEN geldiği + NE ZAMAN. Eskiden kaynak en alttaki
              gri satırdaydı, saat hiç yoktu. */}
          <span className="bt-toast-top">
            <span className="bt-toast-src">{toast.source}</span>
            {saat ? <span className="bt-toast-time">{saat}</span> : null}
          </span>
          <span className="bt-toast-title">{toast.title || "Bildirim"}</span>
          {toast.body ? <span className="bt-toast-msg">{toast.body}</span> : null}
          {toast.actor ? <span className="bt-toast-actor">{toast.actor}</span> : null}
        </span>
      </button>
      <button
        type="button"
        className="bt-toast-close"
        aria-label="Kapat"
        title="Kapat"
        onClick={() => onClose(toast.id)}
      >
        <XMarkIcon />
      </button>
    </div>
  );
}

export default function NotificationToaster() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Kullanıcı kararı (2026-07-15): Bildirim Merkezi modül — kapalıyken toast yok.
  const bildirimModulu = useModuleEnabled("bildirim");

  const items = useSelector((s) => s?.belediyeNotifications?.items || []);
  const toastEnabled = useSelector(selectToastEnabled);
  const soundEnabled = useSelector(selectSoundEnabled);
  const mutedCategories = useSelector(selectMutedCategories);
  // FAZ-79: sessiz saat aralığı (tercihlerden).
  const quietStart = useSelector((s) => s?.belediyeNotifications?.preferences?.quiet_hours_start);
  const quietEnd = useSelector((s) => s?.belediyeNotifications?.preferences?.quiet_hours_end);

  const mobil = useMobil();
  const maxGorunur = mobil ? MAX_VISIBLE_MOBILE : MAX_VISIBLE_WIDE;

  const [toasts, setToasts] = useState([]);
  // FAZ-79: ekrana sığmayanlar burada bekler (state — çip sayısı buradan okunur).
  const [queue, setQueue] = useState([]);
  const toastedRef = useRef(loadToasted());
  const hwmRef = useRef(loadHwm());
  const firstRunRef = useRef(true);

  // FAZ-106 — DEFTER OKUNMADAN TOAST ÇİZİLMEZ.
  //
  // Defter (IndexedDB) asenkron açılır; ilk `items` genellikle daha önce gelir.
  // Beklemezsek telefonda zaten görülmüş bildirimler bir kez daha patlar ve
  // ondan sonra "toast'landı" damgasını yediği için düzeltme şansı da kalmaz.
  // Bir kereye mahsus birkaç yüz milisaniyelik gecikme, yanlış kesintiden
  // ucuzdur.
  //
  // BAŞLANGIÇ DEĞERİ ORTAMA BAKAR: defter tutulamayan ortamda (IndexedDB yok —
  // eski tarayıcı, bazı gizli pencere kipleri, test ortamı) beklenecek bir şey
  // yoktur; kapı açık doğar ve toast eskisi gibi ilk çizimde görünür. Kapıyı
  // koşulsuz kapalı başlatmak, telefon bildirimi HİÇ alamayan bir cihazda bile
  // her açılışta bir tur gecikme demekti.
  const [defterHazir, setDefterHazir] = useState(() => !defterKullanilabilir());

  const canRender = useMemo(() => readUserIdSafe() > 0, []) && bildirimModulu;

  // ── FAZ-83 — "AYNI TOAST DEFALARCA GELİYOR" DÜZELTMESİ ──────────────────
  //
  // Kullanıcı (2026-08-01): "bir toast bir defa gelmeli, okunsa da okunmasa
  // da; okunana kadar defalarca toast geliyor."
  //
  // Sebep BURADAYDI. Eskiden burada bir "taze oturum" kuralı vardı: sekmeye
  // özel `sessionStorage` işareti kullanıcı kimliğiyle uyuşmuyorsa toast
  // geçmişi (`localStorage`) SİLİNİYORDU — "giriş yapınca bekleyenler yeniden
  // yağsın" niyetiyle. Ama `sessionStorage` sekme başına tutulur: her yeni
  // sekme, her tarayıcı açılışı, her yeni oturum TAZE sayılıyordu. Sonuç:
  // okunmamış kalan bir bildirim, okunana kadar HER açılışta yeniden
  // toast'lanıyordu. Toast bir kesinti aracı; kesinti tekrarlanırsa uyarı
  // olmaktan çıkıp gürültü olur ve kullanıcı hepsini görmezden gelmeye başlar.
  //
  // Yeni kural: bir bildirim ömründe BİR KEZ toast'lanır. Okunmuş olması şart
  // değil — okunmamışsa zaten zilde ve merkezde duruyor, kaybolmuyor; yalnız
  // bir daha ekranı kesmiyor. Sıfırlama tamamen kaldırıldı.
  useEffect(() => {
    if (!canRender) return;
    installSoundUnlock();
    dispatch(fetchPreferences());
  }, [dispatch, canRender]);

  // ── FAZ-106 — "TELEFONA DÜŞTÜYSE EKRANA DÜŞMESİN" ───────────────────────
  //
  // Kullanıcı (2026-08-05): "Bir bildirim telefona bildirim olarak düşüyorsa,
  // tekrardan uygulama içinde ana ekrana toast olarak düşmesin."
  //
  // Defteri nöbetçi yazar (public/sw.js), burada yalnız OKUNUR ve mevcut
  // "toast'landı" listesine katılır — yani kural tek yerde: bir bildirim bu
  // cihazda bir kez gösterilir, hangi yüzeyden gösterildiği fark etmez.
  //
  // Bilerek HWM'ye (su seviyesi) yazılmaz: su seviyesi "bu id'nin ALTINDAKİ
  // her şey görüldü" demektir. Telefonda tek bir yeni bildirim görmek,
  // altındaki eski ve hiç görülmemiş satırları da susturmamalı.
  useEffect(() => {
    if (!canRender || !defterKullanilabilir()) {
      setDefterHazir(true);
      return undefined;
    }
    let iptal = false;
    (async () => {
      try {
        const ids = await gosterilenleriOku();
        if (iptal) return;
        ids.forEach((id) => toastedRef.current.add(Number(id)));
      } catch (_) {
        /* defter okunamadı: en kötü ihtimalle toast bir kez fazladan görünür */
      }
      if (!iptal) setDefterHazir(true);
    })();
    return () => {
      iptal = true;
    };
  }, [canRender]);

  // Nöbetçi, uygulama AÇIK ve görünürken telefon bildirimi göstermez; bunun
  // yerine buraya "yeni satır var" der. FAZ-65'te periyodik yoklama
  // kaldırıldığı için bu dürtme olmadan sayfa yeni bildirimi ancak rota
  // değişiminde ya da "Yenile"de öğrenirdi — yani toast dakikalarca gecikirdi.
  useEffect(() => {
    if (!canRender) return undefined;
    let sw = null;
    try {
      sw = navigator?.serviceWorker || null;
    } catch (_) {
      sw = null;
    }
    if (!sw || !sw.addEventListener) return undefined;
    const dinle = (event) => {
      if ((event?.data || {}).tip !== "bildirim-geldi") return;
      dispatch(
        fetchNotifications({ unreadOnly: true, limit: 80, mode: "merge", silent: true })
      );
    };
    try {
      sw.addEventListener("message", dinle);
    } catch (_) {
      return undefined;
    }
    return () => {
      try {
        sw.removeEventListener("message", dinle);
      } catch (_) {}
    };
  }, [canRender, dispatch]);

  // FAZ-79 — "TOAST'LAR GEÇ GELİYOR" DÜZELTMESİ.
  //
  // Kullanıcı (2026-07-30): "ekrana gelen toastlar bazı kullanıcılarda geç
  // geliyor."
  //
  // Sebep: toaster kendi verisini HİÇ çekmiyordu; yalnız Redux'taki `items`i
  // izliyordu. O listeyi dolduran tek yer zil (`NotificationsBell`). FAZ-65'te
  // periyodik yoklama kaldırıldığı için zil de yalnız mount/odak/rota anında
  // çekiyor — yani toast, zilin ilk isteği dönene kadar bekliyordu. Zil
  // herhangi bir sebeple gecikirse (modül bayrağı /me'den geç gelirse, ağ yavaş
  // olursa) toast da o kadar gecikiyordu. Aynı ekranda iki tüketici, tek
  // besleyici.
  //
  // Çözüm: toaster da mount'ta BİR KEZ çeker — ama yalnız henüz hiç
  // çekilmemişse (`lastFetchedAt` boş). Zil zaten çektiyse istek atılmaz, yani
  // çift istek olmaz; zil çekmediyse toast artık ona bağımlı kalmaz.
  const hicCekilmemis = useSelector(
    (s) => !s?.belediyeNotifications?.lastFetchedAt
  );
  const ilkCekimRef = useRef(false);
  useEffect(() => {
    if (!canRender || ilkCekimRef.current) return;
    if (!hicCekilmemis) {
      ilkCekimRef.current = true; // zil hallettiyse bir daha bakmaya gerek yok
      return;
    }
    ilkCekimRef.current = true;
    dispatch(
      fetchNotifications({ unreadOnly: true, limit: 80, mode: "merge", silent: true })
    );
  }, [canRender, hicCekilmemis, dispatch]);

  const isAllowed = useCallback(
    (n) => {
      if (!n || isCancelled(n)) return false;
      if (n.is_read) return false;
      const src = safeStr(n.source_module);
      if (Array.isArray(mutedCategories) && mutedCategories.includes(src)) return false;
      return true;
    },
    [mutedCategories]
  );

  // items değişince yeni adayları topla → toast + ses
  useEffect(() => {
    if (!canRender || !toastEnabled) return;
    // FAZ-106: defter okunmadan aday seçilmez (yukarıdaki gerekçe).
    if (!defterHazir) return;

    // FAZ-79 — SESSİZ SAAT. Aralık içindeyse toast/ses YOK. Adaylar
    // "toast'landı" diye de İŞARETLENMEZ: aksi hâlde gece gelen bildirim sabah
    // da hiç görünmezdi (işaret kalıcı). Sessiz saat kesintiyi erteler, kaydı
    // yok saymaz — bildirim zilde zaten duruyor.
    if (sessizSaatteMi(quietStart, quietEnd)) return;

    const toasted = toastedRef.current;
    const hwm = hwmRef.current;
    const candidates = (Array.isArray(items) ? items : []).filter((n) => {
      const id = Number(n?.id);
      if (!id) return false;
      // İki nöbetçi: kimlik listesi (kesin) + su seviyesi (budanamaz taban).
      if (toasted.has(id)) return false;
      if (id <= hwm) return false;
      return isAllowed(n);
    });

    if (!candidates.length) {
      firstRunRef.current = false;
      return;
    }

    // TÜM adayları toast'lanmış say (ilk yüklemedeki eskiler tekrar patlamasın)
    candidates.forEach((n) => toasted.add(Number(n.id)));
    saveToasted(toasted);

    // Su seviyesini yükselt — kimlik listesi budansa/silinse bile bu id'lerin
    // altındaki hiçbir kayıt bir daha toast'lanamaz.
    const enBuyuk = candidates.reduce((m, n) => Math.max(m, Number(n.id) || 0), hwm);
    if (enBuyuk > hwm) {
      hwmRef.current = enBuyuk;
      saveHwm(enBuyuk);
    }

    // en yeni id'ler önce (items zaten id desc)
    const fresh = candidates
      .map((n) => ({
        id: Number(n.id),
        title: safeStr(n.title),
        // FAZ-85 — toast'ta ÖNCE "hangi kayıt", yoksa olayın metni.
        // Toast bir-iki satırlık bir kesintidir; oraya sığan tek şey varsa o
        // "hangi talep" olmalı — "ne yazıldığı" kaydın kendisinde okunur.
        // Kayıt yoksa (DM mesajı, duyuru) eski davranış: gövde metni.
        body: kayitEtiketi(n) || bildirimNotu(n) || bildirimGovdesi(n),
        level: safeStr(n.level) || "info",
        target_url: safeStr(n.target_url),
        application_id: n.application_id ?? n.payload?.application_id ?? null,
        payload: n.payload || {},
        event: safeStr(n.event),
        actor: actorName(n),                 // gönderen adı-soyadı
        source: sourceLabel(n.source_module),
        source_module: safeStr(n.source_module),
        created_at: safeStr(n.created_at_iso || n.created_at),
      }));

    // FAZ-79: ilk MAX_VISIBLE ekrana, KALANI KUYRUĞA. Eskiden kalan kısım
    // `slice(0, MAX_VISIBLE)` ile sessizce düşüyordu.
    setToasts((prevToasts) => {
      const merged = [...fresh, ...prevToasts];
      const seen = new Set();
      const dedup = [];
      for (const t of merged) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        dedup.push(t);
      }
      const gorunur = dedup.slice(0, maxGorunur);
      const artan = dedup.slice(maxGorunur);
      if (artan.length) {
        setQueue((q) => {
          const varOlan = new Set(q.map((x) => x.id));
          const gorunurIds = new Set(gorunur.map((x) => x.id));
          const yeni = artan.filter(
            (x) => !varOlan.has(x.id) && !gorunurIds.has(x.id)
          );
          return [...q, ...yeni].slice(0, MAX_QUEUE);
        });
      }
      return gorunur;
    });

    // Ses: yeni gelen varsa (tercih açık + en az bir aday muted değil) tek çın.
    //
    // FAZ-51 — ÇİFT SES ÖNLEMİ: mesajlaşma artık bildirim de üretiyor. Sohbetin
    // KENDİ "yeni mesaj" tınısı zaten çalıyor (ChatDock unread-poll'ü). İkisi
    // birden çalarsa tek mesaj iki ses çıkarır. Kural: sesin sahibi en ÖZEL
    // katmandır → mesaj tınısını sohbet çalar, bildirim sesi yalnız mesaj-DIŞI
    // bir aday varsa çalar. Toast görünmeye devam eder (kayıt tek yerde).
    const mesajDisiVar = fresh.some((t) => t.source_module !== "mesajlasma");
    if (soundEnabled && mesajDisiVar) playNotifySound();

    firstRunRef.current = false;
  }, [items, canRender, toastEnabled, soundEnabled, isAllowed, quietStart, quietEnd, maxGorunur, defterHazir]);

  const closeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // FAZ-79 — KUYRUK BESLEMESİ. Ekranda yer açıldıkça kuyruktan bir kart girer
  // ("peş peşe görsün": ilk dördü, biri kapanınca beşinci…).
  //
  // Neden effect: doldurmayı `closeToast` içinde `setToasts(prev => …)`
  // güncelleyicisinin İÇİNDE `setQueue` çağırarak yapmak StrictMode'da
  // güncelleyici iki kez çalıştığı için kuyruktan İKİ kart düşürür (biri
  // kaybolur). Effect, durumun kendisinden türediği için idempotent.
  useEffect(() => {
    if (!queue.length) return;
    if (toasts.length >= maxGorunur) return;
    const bosluk = maxGorunur - toasts.length;
    const girecek = queue.slice(0, bosluk);
    setQueue((q) => q.slice(girecek.length));
    setToasts((prev) => {
      const varOlan = new Set(prev.map((t) => t.id));
      return [...prev, ...girecek.filter((t) => !varOlan.has(t.id))];
    });
  }, [queue, toasts.length, maxGorunur]);

  // FAZ-83 — tavan DARALIRSA (pencere küçüldü / telefon dikey döndü) ekrandaki
  // fazlalık atılmaz, kuyruğun BAŞINA döner: sıradaki yer açıldığında yine
  // gösterilir. Aksi hâlde döndürme bildirim yutardı.
  useEffect(() => {
    if (toasts.length <= maxGorunur) return;
    const fazla = toasts.slice(maxGorunur);
    setToasts((prev) => prev.slice(0, maxGorunur));
    setQueue((q) => {
      const varOlan = new Set(q.map((x) => x.id));
      return [...fazla.filter((x) => !varOlan.has(x.id)), ...q].slice(0, MAX_QUEUE);
    });
  }, [toasts, maxGorunur]);

  const clickToast = useCallback(
    (toast) => {
      const nid = Number(toast.id);
      // Dismiss ≠ okundu; TIKLAMA okundu yapar.
      if (nid) dispatch(markRead({ ids: [nid] }));
      // FAZ-47: yönlendirme ortak çözücüde (zil/toaster/sayfa tek mantık).
      // FAZ-51: mesaj toast'ı sohbeti DOCK'ta açar — eskiden allowChat kapalıydı
      // ve mesaj bildirimi diye bir şey olmadığı için fark etmiyordu; artık
      // ediyor (aksi hâlde toast /yonetim/mesajlar rotasına savuruyordu).
      const target = resolveNotificationTarget(toast, { allowChat: true });
      closeToast(nid);
      if (!target) return;
      if (target.kind === "chat") openChat(target.conversationId);
      else navigate(target.path);
    },
    [dispatch, navigate, closeToast]
  );

  // FAZ-79: kuyrukta bekleyenleri tek hamlede kapat + merkeze git.
  const tumunuGor = useCallback(() => {
    setQueue([]);
    setToasts([]);
    navigate("/yonetim/bildirimler");
  }, [navigate]);

  if (!canRender || !toasts.length) return null;

  return (
    <div className="bt-toaster" aria-live="polite" aria-atomic="false">
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onClose={closeToast} onClick={clickToast} />
      ))}

      {/* FAZ-79 — "+N bildirim daha" ÇİPİ.
          Kuyrukta bekleyen varsa yığının altında durur. İki iş yapar:
            • sayıyı söyler → kullanıcı "dört tane var" sanmaz
            • tıklanınca merkeze götürür → "hepsini görebilsin" isteğinin
              karşılığı (kuyruğu tek tek beklemek zorunda kalmaz)
          Kart DEĞİL, bilinçli olarak daha küçük ve sessiz: yığının kendisiyle
          yarışmaması gerekiyor. */}
      {queue.length > 0 ? (
        <button
          type="button"
          className="bt-more"
          onClick={tumunuGor}
          title="Tüm bildirimleri gör"
        >
          <span className="bt-more-count">+{queue.length}</span>
          <span className="bt-more-text">bildirim daha · tümünü gör</span>
        </button>
      ) : null}
    </div>
  );
}
